import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from "recharts";

/* ================= TYPES ================= */

type Range = "24H" | "72H" | "1W" | "1M";

type FilterInfo = {
  duration: string;
  date_range: {
    from: string;
    to: string;
  };
};

interface HistoricalVitalsProps {
  username: string;
  token: string | null;
}

/* ================= CONFIG ================= */

const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

const rangeToDuration: Record<Range, string> = {
  "24H": "1d",
  "72H": "3d",
  "1W": "7d",
  "1M": "1M",
};

/* ================= MOCK DATA ================= */

const bpData = [
  { time: "01", sys: 120, dia: 80 },
  { time: "02", sys: 128, dia: 82 },
  { time: "03", sys: 135, dia: 85 },
  { time: "04", sys: 140, dia: 90 },
];

const tempData = [
  { time: "01", value: 97.6 },
  { time: "02", value: 98.4 },
  { time: "03", value: 99.1 },
];

const spo2Data = [
  { time: "01", value: 97 },
  { time: "02", value: 96 },
  { time: "03", value: 98 },
  { time: "04", value: 97 },
];

const respData = [
  { time: "01", value: 14 },
  { time: "02", value: 16 },
  { time: "03", value: 18 },
  { time: "04", value: 15 },
];

/* ================= RANGE TOGGLE ================= */

const RangeToggle = ({
  value,
  onChange,
}: {
  value: Range;
  onChange: (v: Range) => void;
}) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={value}
    onChange={(_, v) => v && onChange(v)}
  >
    <ToggleButton value="24H">24H</ToggleButton>
    <ToggleButton value="72H">72H</ToggleButton>
    <ToggleButton value="1W">1W</ToggleButton>
    <ToggleButton value="1M">1M</ToggleButton>
  </ToggleButtonGroup>
);

/* ================= FILTER META ================= */

const FilterMeta = ({ filter }: { filter?: FilterInfo }) => {
  if (!filter) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      {filter.date_range.from} – {filter.date_range.to} ({filter.duration})
    </Typography>
  );
};

/* ================= DOT VARIATION HELPERS ================= */

const getDotColor = (bpm: number) => {
  if (bpm >= 120) return "#ef4444";
  if (bpm >= 100) return "#f97316";
  if (bpm >= 80) return "#22c55e";
  return "#3b82f6";
};

const getDotSize = (bpm: number) => {
  if (bpm >= 120) return 6;
  if (bpm >= 100) return 5;
  if (bpm >= 80) return 4;
  return 3;
};

/* ================= HEART RATE MAPPER ================= */

const mapHeartRateForChart = (records: any[] = []) =>
  records.flatMap((day) =>
    day.data.map((item: any) => ({
      time: new Date(item.takenAt).getTime(),
      bpm: item.bpm,
    }))
  );

/* ================= MAIN COMPONENT ================= */

const HistoricalVitals: React.FC<HistoricalVitalsProps> = ({
  username,
  token,
}) => {
  const [hrLoading, setHrLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, FilterInfo>>({});
  const [heartRateData, setHeartRateData] = useState<any[]>([]);

  const [heartRateRange, setHeartRateRange] = useState<Range>("1W");
  const [bpRange, setBpRange] = useState<Range>("24H");
  const [tempRange, setTempRange] = useState<Range>("24H");
  const [spo2Range, setSpo2Range] = useState<Range>("24H");
  const [respRange, setRespRange] = useState<Range>("24H");
  const [ecgRange, setEcgRange] = useState<Range>("24H");

  /* ================= HEART RATE API ================= */

  useEffect(() => {
    if (!username || !token) return;

    const fetchHeartRate = async () => {
      try {
        setHrLoading(true);
        const res = await axios.post(
          `${BASE_URL}/vitals/intraday`,
          {
            username,
            vitals: [
              {
                type: "HEART_RATE",
                duration: rangeToDuration[heartRateRange],
              },
            ],
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const apiData = res.data?.data;
        setFilters(apiData?.filters_applied || {});
        setHeartRateData(
          mapHeartRateForChart(apiData?.vitals?.HEART_RATE || [])
        );
      } catch (err) {
        console.error("Heart rate API failed", err);
      } finally {
        setHrLoading(false);
      }
    };

    fetchHeartRate();
  }, [heartRateRange, username, token]);

  /* ================= UI ================= */

  return (
    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
      {/* ================= HEART RATE ================= */}
      <Paper sx={{ p: 2, gridColumn: "1 / -1" }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>❤️ Heart Rate</Typography>
          <RangeToggle value={heartRateRange} onChange={setHeartRateRange} />
        </Box>

        <FilterMeta filter={filters.HEART_RATE} />

        <Box position="relative" mt={2}>
          {hrLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255,255,255,0.6)"
              zIndex={1}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis
                dataKey="time"
                type="number"
                scale="time"
                domain={["auto", "auto"]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) =>
                  new Date(v).toLocaleString("en-US", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                }
              />
              <YAxis dataKey="bpm" domain={[40, 160]} unit=" bpm" />
              <Tooltip
  
  formatter={(value: any) => [`${value}`]}
/>
              <Scatter
                data={heartRateData}
                shape={({ cx, cy, payload }: any) => (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={getDotSize(payload.bpm)}
                    fill={getDotColor(payload.bpm)}
                  />
                )}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ================= BLOOD PRESSURE ================= */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>🩸 Blood Pressure</Typography>
          <RangeToggle value={bpRange} onChange={setBpRange} />
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={bpData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Line dataKey="sys" stroke="#60a5fa" dot />
            <Line dataKey="dia" stroke="#f87171" dot />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* ================= TEMPERATURE ================= */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>🌡️ Temperature</Typography>
          <RangeToggle value={tempRange} onChange={setTempRange} />
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={tempData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis domain={[94, 102]} />
            <Tooltip />
            <Line dataKey="value" stroke="#60a5fa" dot />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* ================= SPO2 ================= */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>💨 SpO₂</Typography>
          <RangeToggle value={spo2Range} onChange={setSpo2Range} />
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={spo2Data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis domain={[90, 100]} unit="%" />
            <Tooltip />
            <Line dataKey="value" stroke="#3b82f6" dot />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* ================= RESPIRATORY RATE ================= */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>🫁 Respiratory Rate</Typography>
          <RangeToggle value={respRange} onChange={setRespRange} />
        </Box>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={respData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis unit="/min" />
            <Tooltip />
            <Line dataKey="value" stroke="#f59e0b" dot />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* ================= ECG ================= */}
      <Paper sx={{ p: 2, gridColumn: "1 / -1" }}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={600}>❤️ ECG</Typography>
          <RangeToggle value={ecgRange} onChange={setEcgRange} />
        </Box>
        <Box
          sx={{
            height: 160,
            background:
              "repeating-linear-gradient(0deg,#eee,#eee 1px,transparent 1px,transparent 10px)",
          }}
        />
      </Paper>
    </Box>
  );
};

export default HistoricalVitals;
